/**
 * Staff-only referral leaderboard. Ranks internal Upstand team members by the companies (and their
 * users) they've brought in. Attribution is manual + per-company: `companies.referred_by_staff`,
 * assigned in the "Assign referrals" panel below. Every query/mutation is a SECURITY DEFINER RPC
 * gated on is_staff(auth.uid()) (migration 0103) - this page is a convenience surface, not the
 * security boundary. App.tsx only routes here for staff; a non-staff caller hitting the RPCs
 * directly gets a 42501 and this page renders an access-denied state.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trophy, Medal, Building2, Users2, Search, ShieldCheck, Loader2, UserCog } from 'lucide-react';
import { supabase } from '@saas-boilerplate/shared';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';

const NO_REFERRER = '__none__';

type LeaderRow = {
  staff_id: string;
  full_name: string | null;
  email: string | null;
  companies: number;
  users: number;
};

type CompanyUser = { id: string; name: string | null; email: string | null };

type CompanyRow = {
  company_id: string;
  company_name: string | null;
  referred_by_staff: string | null;
  referrer_name: string | null;
  users: number;
  users_list: CompanyUser[];
  created_at: string | null;
};

type StaffRow = { staff_id: string; full_name: string | null; email: string | null };

type ProfileRow = { id: string; full_name: string | null; email: string | null; is_staff: boolean };

function displayName(name: string | null, email: string | null): string {
  return (name && name.trim()) || (email && email.trim()) || 'Unknown';
}

function rankAccent(rank: number): { icon: typeof Trophy | null; className: string } {
  if (rank === 1) return { icon: Trophy, className: 'text-amber-500' };
  if (rank === 2) return { icon: Medal, className: 'text-stone-400' };
  if (rank === 3) return { icon: Medal, className: 'text-amber-700/70' };
  return { icon: null, className: 'text-muted-foreground' };
}

export function LeaderboardPage() {
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [leaders, setLeaders] = useState<LeaderRow[]>([]);
  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [staff, setStaff] = useState<StaffRow[]>([]);
  const [profiles, setProfiles] = useState<ProfileRow[]>([]);
  const [companyQuery, setCompanyQuery] = useState('');
  const [staffQuery, setStaffQuery] = useState('');
  const [busyCompany, setBusyCompany] = useState<string | null>(null);
  const [busyProfile, setBusyProfile] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [lb, co, st, pr] = await Promise.all([
      supabase.rpc('get_referral_leaderboard'),
      supabase.rpc('get_referral_companies'),
      supabase.rpc('get_staff_members'),
      supabase.rpc('get_all_profiles_for_staff'),
    ]);
    const firstError = lb.error || co.error || st.error || pr.error;
    if (firstError) {
      // 42501 = the RPC's staff gate rejected the caller.
      if ((firstError as { code?: string }).code === '42501' || /not authorized/i.test(firstError.message)) {
        setDenied(true);
      } else {
        toast.error(`Failed to load leaderboard: ${firstError.message}`);
      }
      return;
    }
    setLeaders((lb.data as LeaderRow[]) || []);
    setCompanies((co.data as CompanyRow[]) || []);
    setStaff((st.data as StaffRow[]) || []);
    setProfiles((pr.data as ProfileRow[]) || []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await load();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  const assignReferrer = useCallback(
    async (companyId: string, staffId: string | null) => {
      setBusyCompany(companyId);
      // Optimistic: reflect the new referrer immediately in the companies list.
      setCompanies((prev) =>
        prev.map((c) =>
          c.company_id === companyId
            ? {
                ...c,
                referred_by_staff: staffId,
                referrer_name: staffId
                  ? staff.find((s) => s.staff_id === staffId)?.full_name ?? null
                  : null,
              }
            : c,
        ),
      );
      const { error } = await supabase.rpc('set_company_referrer', {
        p_company_id: companyId,
        p_staff_id: staffId,
      });
      if (error) {
        toast.error(`Couldn't save referrer: ${error.message}`);
      } else {
        toast.success('Referral updated');
      }
      // Reload so the leaderboard totals reflect the change (and to reconcile the optimistic edit).
      await load();
      setBusyCompany(null);
    },
    [load, staff],
  );

  const toggleStaff = useCallback(
    async (userId: string, next: boolean) => {
      setBusyProfile(userId);
      setProfiles((prev) => prev.map((p) => (p.id === userId ? { ...p, is_staff: next } : p)));
      const { error } = await supabase.rpc('set_user_staff', { p_user_id: userId, p_is_staff: next });
      if (error) {
        toast.error(`Couldn't update staff: ${error.message}`);
      } else {
        toast.success(next ? 'Marked as staff' : 'Removed from staff');
      }
      await load();
      setBusyProfile(null);
    },
    [load],
  );

  const filteredCompanies = useMemo(() => {
    const q = companyQuery.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter(
      (c) =>
        (c.company_name || '').toLowerCase().includes(q) ||
        (c.referrer_name || '').toLowerCase().includes(q),
    );
  }, [companies, companyQuery]);

  const filteredProfiles = useMemo(() => {
    const q = staffQuery.trim().toLowerCase();
    if (!q) return profiles;
    return profiles.filter(
      (p) => (p.full_name || '').toLowerCase().includes(q) || (p.email || '').toLowerCase().includes(q),
    );
  }, [profiles, staffQuery]);

  const totals = useMemo(
    () => ({
      companies: leaders.reduce((n, r) => n + Number(r.companies || 0), 0),
      users: leaders.reduce((n, r) => n + Number(r.users || 0), 0),
    }),
    [leaders],
  );

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (denied) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-24 text-center">
        <ShieldCheck className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
        <h1 className="mb-2 font-display text-2xl font-light text-headline">Staff only</h1>
        <p className="text-sm text-muted-foreground">
          The referral leaderboard is only available to Upstand staff.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 lg:px-12 lg:py-12">
      <header className="mb-8">
        <p className="mb-2 flex items-center gap-1.5 font-display text-[10px] font-bold uppercase tracking-[0.25em] text-tertiary">
          <ShieldCheck className="h-3 w-3" /> Staff
        </p>
        <h1 className="mb-3 font-display text-3xl font-light leading-tight tracking-tight text-headline md:text-4xl">
          Referral leaderboard
        </h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Who's brought in the most companies and users. Attribution is set per company in{' '}
          <span className="font-medium text-foreground/80">Assign referrals</span> below.
        </p>
      </header>

      {/* Leaderboard */}
      <Card className="mb-8 border-outline-variant/30">
        <CardHeader>
          <CardTitle className="font-display text-lg font-semibold text-headline">Rankings</CardTitle>
          <CardDescription>
            {leaders.length} staff · {totals.companies} companies · {totals.users} users attributed
          </CardDescription>
        </CardHeader>
        <CardContent>
          {leaders.length === 0 ? (
            <p className="py-6 text-sm italic text-muted-foreground">No staff members yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Rank</TableHead>
                  <TableHead>Team member</TableHead>
                  <TableHead className="text-right">Companies</TableHead>
                  <TableHead className="text-right">Users</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leaders.map((r, i) => {
                  const rank = i + 1;
                  const accent = rankAccent(rank);
                  const Icon = accent.icon;
                  return (
                    <TableRow key={r.staff_id}>
                      <TableCell>
                        <span className={`flex items-center gap-1.5 font-mono text-sm font-semibold tabular-nums ${accent.className}`}>
                          {Icon ? <Icon className="h-4 w-4" /> : null}
                          {rank}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-foreground">{displayName(r.full_name, r.email)}</div>
                        {r.full_name && r.email ? (
                          <div className="text-xs text-muted-foreground">{r.email}</div>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="inline-flex items-center gap-1.5 font-semibold tabular-nums text-headline">
                          <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                          {Number(r.companies || 0)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="inline-flex items-center gap-1.5 font-semibold tabular-nums text-headline">
                          <Users2 className="h-3.5 w-3.5 text-muted-foreground" />
                          {Number(r.users || 0)}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Assign referrals */}
      <Card className="mb-8 border-outline-variant/30">
        <CardHeader>
          <CardTitle className="font-display text-lg font-semibold text-headline">Assign referrals</CardTitle>
          <CardDescription>
            Set which team member brought each company in. Their users count automatically.
          </CardDescription>
          <div className="relative mt-3 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={companyQuery}
              onChange={(e) => setCompanyQuery(e.target.value)}
              placeholder="Search companies…"
              className="h-9 pl-8"
            />
          </div>
        </CardHeader>
        <CardContent>
          {filteredCompanies.length === 0 ? (
            <p className="py-6 text-sm italic text-muted-foreground">
              {companies.length === 0 ? 'No companies yet.' : 'No companies match your search.'}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead className="text-right">Users</TableHead>
                  <TableHead className="w-64">Referred by</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCompanies.map((c) => (
                  <TableRow key={c.company_id}>
                    <TableCell className="font-medium text-foreground">
                      {c.company_name || 'Untitled company'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {c.users_list && c.users_list.length > 0 ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help underline decoration-dotted decoration-muted-foreground/50 underline-offset-2">
                              {Number(c.users || 0)}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="left" className="max-w-xs">
                            <div className="max-h-56 space-y-0.5 overflow-y-auto text-left">
                              {c.users_list.map((u) => (
                                <div key={u.id} className="whitespace-nowrap">
                                  {displayName(u.name, u.email)}
                                  {u.name && u.email ? (
                                    <span className="opacity-70"> · {u.email}</span>
                                  ) : null}
                                </div>
                              ))}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        Number(c.users || 0)
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Select
                          value={c.referred_by_staff ?? NO_REFERRER}
                          onValueChange={(v) => assignReferrer(c.company_id, v === NO_REFERRER ? null : v)}
                          disabled={busyCompany === c.company_id}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Unassigned" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NO_REFERRER}>Unassigned</SelectItem>
                            {staff.map((s) => (
                              <SelectItem key={s.staff_id} value={s.staff_id}>
                                {displayName(s.full_name, s.email)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {busyCompany === c.company_id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Manage staff */}
      <Card className="border-outline-variant/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-display text-lg font-semibold text-headline">
            <UserCog className="h-4 w-4" /> Manage staff
          </CardTitle>
          <CardDescription>
            Toggle who counts as Upstand staff. Staff can see this leaderboard and be credited with referrals.
          </CardDescription>
          <div className="relative mt-3 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={staffQuery}
              onChange={(e) => setStaffQuery(e.target.value)}
              placeholder="Search people…"
              className="h-9 pl-8"
            />
          </div>
        </CardHeader>
        <CardContent>
          {filteredProfiles.length === 0 ? (
            <p className="py-6 text-sm italic text-muted-foreground">No people match your search.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Person</TableHead>
                  <TableHead className="w-32 text-right">Staff</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProfiles.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <div className="font-medium text-foreground">{displayName(p.full_name, p.email)}</div>
                      {p.full_name && p.email ? (
                        <div className="text-xs text-muted-foreground">{p.email}</div>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {busyProfile === p.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                        ) : null}
                        <Switch
                          checked={p.is_staff}
                          disabled={busyProfile === p.id}
                          onCheckedChange={(checked) => toggleStaff(p.id, checked)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
